import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { FiCommand, FiMoon, FiSun, FiArrowUpRight } from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";
import { useIntro } from "../../context/IntroContext";
import useActiveSection from "../../hooks/useActiveSection";
import { ease } from "../../utils/motionPresets";
import "./nav.css";

export const navLinks = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "work", label: "Work" },
  { id: "experience", label: "Experience" },
  { id: "stack", label: "Stack" },
  { id: "contact", label: "Contact" }
];

const sectionIds = navLinks.map((link) => link.id);
const MotionLink = (motion.create || motion)(Link);

const Nav = ({ onOpenPalette }) => {
  const { theme, toggleTheme } = useTheme();
  const { ready } = useIntro();
  const { pathname } = useLocation();
  const sectionActive = useActiveSection(sectionIds);
  // Case-study pages live under "Work".
  const active = pathname.startsWith("/work/") ? "work" : sectionActive;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    const onKey = (event) => event.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <>
      <motion.header
        className={`nav ${scrolled ? "nav--scrolled" : ""}`}
        initial={{ y: -40, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : undefined}
        transition={{ duration: 0.9, ease, delay: 0.2 }}
      >
        <div className="nav__inner">
          <Link to="/#top" className="nav__brand" aria-label="Yaseen Al-Midani — home">
            <span className="nav__mark" aria-hidden="true">
              YA
            </span>
            <span className="nav__name">Yaseen Al-Midani</span>
          </Link>

          <nav className="nav__links" aria-label="Primary">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                to={`/#${link.id}`}
                className={`nav__link ${active === link.id ? "is-active" : ""}`}
                aria-current={active === link.id ? "true" : undefined}
              >
                {active === link.id && (
                  <motion.span
                    layoutId="nav-pill"
                    className="nav__pill"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className="nav__link-label">{link.label}</span>
              </Link>
            ))}
          </nav>

          <div className="nav__actions">
            <button
              type="button"
              className="nav__icon-btn nav__palette"
              onClick={onOpenPalette}
              aria-label="Open command menu"
            >
              <FiCommand />
              <span className="nav__palette-hint">
                <kbd>{isMac ? "⌘" : "Ctrl"}</kbd>
                <kbd>K</kbd>
              </span>
            </button>
            <button
              type="button"
              className="nav__icon-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ display: "grid" }}
                >
                  {theme === "dark" ? <FiSun /> : <FiMoon />}
                </motion.span>
              </AnimatePresence>
            </button>
            <Link to="/#contact" className="btn btn--primary btn--sm nav__cta">
              Hire me
            </Link>
            <button
              type="button"
              className={`nav__burger ${menuOpen ? "is-open" : ""}`}
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="mobile-menu"
            className="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease }}
          >
            <nav aria-label="Mobile">
              {navLinks.map((link, i) => (
                <MotionLink
                  key={link.id}
                  to={`/#${link.id}`}
                  className="mobile-menu__link"
                  onClick={() => setMenuOpen(false)}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, ease, delay: 0.15 + i * 0.05 }}
                >
                  <span className="mobile-menu__index">0{i + 1}</span>
                  {link.label}
                </MotionLink>
              ))}
            </nav>
            <MotionLink
              to="/#contact"
              className="btn btn--primary mobile-menu__cta"
              onClick={() => setMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Start a project <FiArrowUpRight />
            </MotionLink>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
